import {
  BooleanInput,
  DateTimeInput,
  NumberInput,
  required,
  SelectInput,
  TextInput,
  useTranslate,
  useNotify,
  FunctionField,
} from 'react-admin';
import { Box, Stack, Typography, Button, CircularProgress } from '@mui/material';
import { Resources } from '../resource';
import { useFormContext } from 'react-hook-form';
import { useRef, useState, useEffect } from 'react';
import { smallBannersUploadByServer } from '../../services/smallBannersUpload';
import { smallBannersDataProvider } from './dataProvider';
interface FormProps {
  isCreate: boolean;
}

const BannerForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();

  const numberProps = {
    parse: (v: any) => (v === '' || v == null ? undefined : Number(v)),
    format: (v: any) => (v == null ? '' : v),
    inputProps: {
      inputMode: 'decimal',
      step: 'any',
      onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
        (e.currentTarget as HTMLInputElement).blur();
      },
    } as const,
    onWheel: (e: React.WheelEvent) => {
      const input = (e.currentTarget as HTMLElement).querySelector('input');
      if (input) (input as HTMLInputElement).blur();
    },
  } as const;

  const { setValue, watch } = useFormContext();
  const notify = useNotify();
  const fileENInputRef = useRef<HTMLInputElement | null>(null);
  const fileZHInputRef = useRef<HTMLInputElement | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadCfg, setUploadCfg] = useState<any>(null);

  const enImageUrlValue = watch('enImageUrl');
  const zhImageUrlValue = watch('zhImageUrl');

  // 页面加载时请求一次 uploadCfg
  useEffect(() => {
    const fetchUploadCfg = async () => {
      try {
        const cfg = await smallBannersDataProvider.getUploadCfg(Resources.smallBanners);
        setUploadCfg(cfg);
      } catch (error) {
        console.error('Failed to fetch upload config:', error);
      }
    };
    fetchUploadCfg();
  }, []);

  const handleENSelectFile = () => {
    fileENInputRef.current?.click();
  };
  const handleZHSelectFile = () => {
    fileZHInputRef.current?.click();
  };
  // 通用的上传函数
  const handleFileUpload = async (
    file: File,
    fieldName: string,
    inputRef: React.RefObject<HTMLInputElement>,
    setUploadingState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!uploadCfg) {
      notify('Upload config not loaded yet', { type: 'error' });
      return;
    }

    setUploadingState(true);

    try {
      const result = await smallBannersUploadByServer(file, {
        uploadUrl: uploadCfg?.uploadUrl,
        fileType: uploadCfg?.fileType,
        tags: uploadCfg?.tags,
        pathPrefix: uploadCfg?.pathPrefix,
        customFilename: file.name,
      });
      const uploadedUrl = result?.file?.publicURL || result?.publicURL;
      if (!uploadedUrl) {
        console.error('data structure returned by upload:', result);
        notify('upload failed image url not obtained', { type: 'error' });
        throw new Error('Upload succeeded but no url returned');
      }
      setValue(fieldName, uploadedUrl, { shouldDirty: true, shouldValidate: true });
      notify('UPLOAD SUCCESSFUL', { type: 'success' });
      return result;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'UPLOAD FAILED';
      notify(errorMessage, { type: 'error' });
    } finally {
      setUploadingState(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleENUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFileUpload(file, 'enImageUrl', fileENInputRef, setUploading);
  };
  const handleZHUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFileUpload(file, 'zhImageUrl', fileZHInputRef, setUploading);
  };

  const formatWithToISOString = (date: Date) => {
    // 步骤1：修正时区（东八区）
    const offset = date.getTimezoneOffset() * 60000; // 东八区为-480分钟 → -28800000毫秒
    const localDate = new Date(date.getTime() - offset);

    // 步骤2：调用toISOString() + 去掉Z后缀 + 裁剪毫秒
    const isoStr = localDate.toISOString(); // 此时是东八区的ISO串：2025-12-18T16:14:00.000Z
    const formatted = isoStr.replace('.000Z', ''); // 去掉毫秒和Z → 2025-12-18T16:14:00

    return formatted;
  };

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.category')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="category"
              validate={[required()]}
              placeholder={t('module.smallBanners.category')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.route')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="route"
              validate={[]}
              placeholder={t('module.smallBanners.route')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.isInternalRoute')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput
              label=""
              source="isInternalRoute"
              helperText={<span style={{ color: 'red' }}>{''}</span>}
              format={(v) => v === true || v === 1}
              parse={(v) => v}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.platform')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              source="platform"
              label={false}
              choices={[
                { id: 'PC', name: 'PC' },
                { id: 'APP', name: 'APP' },
                { id: 'H5', name: 'H5' },
              ]}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.enImageUrl')}
          </Typography>
          <FunctionField
            label="module.smallBanners.enImageUrl"
            render={(record: any) => (
              <Box
                component="img"
                src={enImageUrlValue || record.enImageUrl}
                alt="Banner"
                sx={{ maxWidth: 100, maxHeight: 50, objectFit: 'contain' }}
              />
            )}
            sortable={false}
          />
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextInput
              label={false}
              source="enImageUrl"
              validate={[required()]}
              placeholder={t('module.smallBanners.enImageUrl')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
            <input
              ref={fileENInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/svg" // 仅允许支持的格式
              style={{ display: 'none' }}
              onChange={handleENUpload}
            />
            <Button variant="outlined" onClick={handleENSelectFile} disabled={uploading}>
              {uploading ? <CircularProgress size={18} /> : t('module.smallBanners.upload')}
            </Button>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.zhImageUrl')}
          </Typography>
          <FunctionField
            label="module.smallBanners.zhImageUrl"
            render={(record: any) => (
              <Box
                component="img"
                src={zhImageUrlValue || record.zhImageUrl}
                alt="Banner"
                sx={{ maxWidth: 100, maxHeight: 50, objectFit: 'contain' }}
              />
            )}
            sortable={false}
          />
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextInput
              label={false}
              source="zhImageUrl"
              validate={[required()]}
              placeholder={t('module.smallBanners.zhImageUrl')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
            <input
              ref={fileZHInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/svg" // 仅允许支持的格式
              style={{ display: 'none' }}
              onChange={handleZHUpload}
            />
            <Button variant="outlined" onClick={handleZHSelectFile} disabled={uploading}>
              {uploading ? <CircularProgress size={18} /> : t('module.smallBanners.upload')}
            </Button>
          </Box>
        </Stack>



        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.showButton')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput
              label=""
              source="showButton"
              helperText={<span style={{ color: 'red' }}>{''}</span>}
              format={(v) => v === true || v === 1}
              parse={(v) => v}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.enButtonText')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="enButtonText"
              validate={[]}
              placeholder={t('module.smallBanners.enButtonText')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.zhButtonText')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="zhButtonText"
              validate={[]}
              placeholder={t('module.smallBanners.zhButtonText')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>


        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.startAt')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <DateTimeInput
              label={false}
              source="startAt"
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              // UI 显示 ISO 字符串，提交为毫秒时间戳
              format={(value: any) => {
                if (value == null || value === '') return '';
                const num = Number(value);
                const date = Number.isNaN(num) ? new Date(value) : new Date(num);
                return formatWithToISOString(date);
              }}
              parse={(value: any) => {
                if (!value) return undefined;
                const ts = new Date(value).getTime();
                return Number.isNaN(ts) ? undefined : ts;
              }}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.endAt')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <DateTimeInput
              label={false}
              source="endAt"
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              // UI 显示 ISO 字符串，提交为毫秒时间戳
              format={(value: any) => {
                if (value == null || value === '') return '';
                const num = Number(value);
                const date = Number.isNaN(num) ? new Date(value) : new Date(num);
                return formatWithToISOString(date);
              }}
              parse={(value: any) => {
                if (!value) return undefined;
                const ts = new Date(value).getTime(); //ms
                return Number.isNaN(ts) ? undefined : ts;
              }}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.sortIndex')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="sortIndex"
              validate={[required()]}
              placeholder={t('module.smallBanners.sortIndex')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.smallBanners.isActive')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput
              label=""
              source="isActive"
              helperText={<span style={{ color: 'red' }}>{''}</span>}
              format={(v) => v === true || v === 1} // from api data / from parse
              parse={(v) => (v ? 1 : -1)} // parse to api
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

// @ts-ignore
export default BannerForm;
